import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse, HttpContext } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EnvironmentService } from '@core/services/environment.service';

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ApiRequestOptions {
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  withCredentials?: boolean;
  context?: HttpContext;
}

@Injectable({
  providedIn: 'root'
})
export class ApiBaseService {
  private readonly http = inject(HttpClient);
  private readonly environmentService = inject(EnvironmentService);
  protected readonly baseUrl = this.environmentService.apiBaseUrl;

  /**
   * Realizar petición GET
   */
  protected get<T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Observable<T> {
  const url = this.buildUrl(endpoint);
  const httpParams = this.buildHttpParams(options.params);

  return this.http.get<T>(url, {
    params: httpParams,
    headers: options.headers,
    withCredentials: options.withCredentials ?? true,
    context: options.context
  }).pipe(
    catchError(this.handleError)
  );
}

  /**
   * Realizar petición POST
   */
  protected post<T>(
    endpoint: string,
    body: unknown,
    options: ApiRequestOptions = {}
  ): Observable<T> {
    const url = this.buildUrl(endpoint);
    const httpParams = this.buildHttpParams(options.params);

    return this.http.post<T>(url, body, {
      params: httpParams,
      headers: options.headers,
      withCredentials: options.withCredentials ?? true,
      context: options.context
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Realizar petición PUT
   */
  protected put<T>(
    endpoint: string,
    body: unknown,
    options: ApiRequestOptions = {}
  ): Observable<T> {
    const url = this.buildUrl(endpoint);
    const httpParams = this.buildHttpParams(options.params);

    return this.http.put<T>(url, body, {
      params: httpParams,
      headers: options.headers,
      withCredentials: options.withCredentials ?? true,
      context: options.context
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Realizar petición DELETE
   */
  protected delete<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Observable<T> {
    const url = this.buildUrl(endpoint);
    const httpParams = this.buildHttpParams(options.params);

    return this.http.delete<T>(url, {
      params: httpParams,
      headers: options.headers,
      withCredentials: options.withCredentials ?? true,
      context: options.context
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Realizar petición POST y esperar una respuesta de TEXTO.
   * Ideal para descargar archivos como CSV o texto plano.
   */
  protected postAndGetResponseAsText(
    endpoint: string,
    body: unknown,
    options: ApiRequestOptions = {}
  ): Observable<string> {
    const url = this.buildUrl(endpoint);
    const httpParams = this.buildHttpParams(options.params);

    return this.http.post(url, body, {
      headers: options.headers,
      params: httpParams,
      withCredentials: options.withCredentials ?? true,
      context: options.context,
      responseType: 'text'
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Construir URL completa
   */
  private buildUrl(endpoint: string): string {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${this.baseUrl}/${cleanEndpoint}`;
  }

  /**
   * Construir HttpParams desde un objeto
   */
  private buildHttpParams(params?: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== null && value !== undefined) {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }

    return httpParams;
  }

  /**
   * Manejo centralizado de errores
   */
  private handleError = (error: HttpErrorResponse): Observable<never> => {
    console.error('API Error:', error);

    let errorMessage = 'Ocurrió un error inesperado';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return throwError(() => new Error(errorMessage));
  };
}
